import {
  CONNECTED_MESSAGE_READ_CONTRACT, CONNECTED_MESSAGE_BODY_LIMIT, CONNECTED_MESSAGE_PAGE_LIMIT,
  connectedMessageText, connectedMessageUuid, connectedMessageAccountId,
  parseConnectedMessageReadRequest,
  type ConnectedMessageItem,
  type ConnectedMessageReadReply,
  type ConnectedMessageReadRequest,
} from "../../supabase/functions/_shared/connectedMessageDomain";
import { localeTagFor, type Lang } from "@/i18n/locale";
import { connectedTaskOwnerSession, invokeConnectedTaskWrite } from "@/lib/connectedTaskExecutor";

export type {
  ConnectedMessageItem, ConnectedMessageProvider, ConnectedMessageReadReply, ConnectedMessageReadRequest,
} from "../../supabase/functions/_shared/connectedMessageDomain";
export interface ConnectedMessageReadIdentity {
  auth_session_id: string;
  connection_id: string;
  account_id: string;
}
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const uuid = connectedMessageUuid;
const natural = (value: unknown, max: number) =>
  Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= max;
const text = (value: unknown, max: number, multiline = false, empty = false) => {
  try { connectedMessageText(value, max, "response", { multiline, empty }); return true; }
  catch { return false; }
};
const validAccount = (provider: ConnectedMessageReadRequest["provider"], value: unknown) => {
  try { connectedMessageAccountId(provider, value); return true; } catch { return false; }
};
const allowed = (value: Record<string, unknown>, keys: string[]) =>
  Object.keys(value).every(key => keys.includes(key));

/** No provider content or unbound reply can become an authenticated read. */
export function validateConnectedMessageRead(
  raw: unknown, request: ConnectedMessageReadRequest, sid: string,
  identity?: ConnectedMessageReadIdentity,
): raw is ConnectedMessageReadReply {
  if (!object(raw) || !allowed(raw, ["ok", "read_contract", "provider", "action", "owner_id",
    "auth_session_id", "connection_id", "account_id", "display_name", "can_write", "items",
    "item", "next_cursor", "coverage"]) || raw.ok !== true ||
    raw.read_contract !== CONNECTED_MESSAGE_READ_CONTRACT || raw.provider !== request.provider ||
    raw.action !== request.action || raw.owner_id !== request.expected_owner_id ||
    raw.auth_session_id !== sid || !uuid(raw.connection_id) || !text(raw.account_id, 200) ||
    !validAccount(request.provider, raw.account_id) ||
    !text(raw.display_name, 200) || typeof raw.can_write !== "boolean" || !object(raw.coverage)) return false;
  if (identity && (identity.auth_session_id !== sid || identity.connection_id !== raw.connection_id ||
    identity.account_id !== raw.account_id)) return false;
  const coverage = raw.coverage;
  if (!allowed(coverage, ["state", "reason", "scanned", "returned", "limit", "body_truncated"]) ||
    !["complete", "partial"].includes(String(coverage.state)) || coverage.limit !== CONNECTED_MESSAGE_PAGE_LIMIT ||
    !natural(coverage.scanned, CONNECTED_MESSAGE_PAGE_LIMIT) || !natural(coverage.returned, CONNECTED_MESSAGE_PAGE_LIMIT) ||
    Number(coverage.returned) > Number(coverage.scanned) || typeof coverage.body_truncated !== "boolean" ||
    coverage.reason !== undefined && !["page_limited", "local_filter", "text_truncated"].includes(String(coverage.reason)) ||
    coverage.state === "complete" && (raw.next_cursor || coverage.reason !== undefined || coverage.body_truncated) ||
    coverage.state === "partial" && coverage.reason === undefined ||
    raw.next_cursor !== undefined && raw.next_cursor !== null &&
      (typeof raw.next_cursor !== "string" || !/^[A-Za-z0-9_-]{1,16000}$/.test(raw.next_cursor) ||
        raw.next_cursor === request.cursor)) return false;
  try {
    const validItem = (value: unknown): value is ConnectedMessageItem => {
      if (!object(value) || !allowed(value, ["id", "title", "kind", "list_id", "body_text", "from",
        "created_at", "thread_id", "reply_count", "truncated"]) ||
        !["channel", "chat", "message"].includes(String(value.kind)) ||
        !text(value.title, 200, value.kind === "message", value.kind === "message") ||
        value.from !== undefined && !text(value.from, 200, false, true) ||
        value.body_text !== undefined && !text(value.body_text, CONNECTED_MESSAGE_BODY_LIMIT, true, true) ||
        value.truncated !== undefined && typeof value.truncated !== "boolean" ||
        value.reply_count !== undefined && !natural(value.reply_count, Number.MAX_SAFE_INTEGER)) return false;
      const base = { read_contract: CONNECTED_MESSAGE_READ_CONTRACT, provider: request.provider,
        expected_owner_id: request.expected_owner_id };
      if (value.kind === "message") {
        parseConnectedMessageReadRequest({ ...base, action: "get", list_id: value.list_id, item_id: value.id });
        if (typeof value.body_text !== "string" || !text(value.created_at, 100)) return false;
        if (request.provider === "slack") {
          if (value.created_at !== value.id) return false;
          if (value.thread_id !== undefined)
            parseConnectedMessageReadRequest({ ...base, action: "thread", list_id: value.list_id, thread_id: value.thread_id });
        } else {
          if (!/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,9})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(String(value.created_at)) ||
            !Number.isFinite(Date.parse(String(value.created_at))) ||
            new Date(String(value.created_at).slice(0, 10) + "T00:00:00Z").toISOString().slice(0, 10) !== String(value.created_at).slice(0, 10) || value.thread_id !== undefined || value.reply_count !== undefined) return false;
        }
        if (value.list_id !== request.list_id || value.truncated === true &&
          !(coverage.body_truncated === true && coverage.state === "partial")) return false;
        if (request.action === "thread" && value.thread_id !== request.thread_id && value.id !== request.thread_id) return false;
      } else {
        parseConnectedMessageReadRequest({ ...base, action: "search", list_id: value.id });
        if (value.list_id !== value.id || value.kind !== (request.provider === "slack" ? "channel" : "chat") ||
          value.body_text !== undefined || value.created_at !== undefined || value.thread_id !== undefined ||
          value.reply_count !== undefined || value.from !== undefined || value.truncated === true &&
            !(coverage.body_truncated === true && coverage.state === "partial")) return false;
      }
      return true;
    };
    if (request.action === "get") return raw.items === undefined &&
      (raw.next_cursor === undefined || raw.next_cursor === null) && validItem(raw.item) &&
      raw.item.kind === "message" && raw.item.id === request.item_id && coverage.returned === 1 &&
      coverage.scanned === 1;
    if (!Array.isArray(raw.items) || raw.item !== undefined || raw.items.length !== coverage.returned ||
      !raw.items.every(validItem) || new Set(raw.items.map(item => item.id)).size !== raw.items.length) return false;
    const kind = request.action === "lists" ? request.provider === "slack" ? "channel" : "chat" : "message";
    return raw.items.every(item => item.kind === kind);
  } catch { return false; }
}

/** Pin owner and active login before and after transport; quoted message text is data. */
export async function requestConnectedMessageRead(
  owner: string,
  body: Omit<ConnectedMessageReadRequest, "read_contract" | "expected_owner_id">,
  options: {
    signal: AbortSignal; isCurrent: () => boolean; identity?: ConnectedMessageReadIdentity;
    surfaceQuota?: (error: unknown) => Promise<boolean>;
  },
): Promise<ConnectedMessageReadReply> {
  const session = await connectedTaskOwnerSession(owner, options.identity?.auth_session_id);
  if (!options.isCurrent() || options.signal.aborted) throw new Error("invalid_session");
  const request = parseConnectedMessageReadRequest({
    ...body, read_contract: CONNECTED_MESSAGE_READ_CONTRACT, expected_owner_id: owner,
  });
  const raw = await invokeConnectedTaskWrite(owner, session.sid, request as unknown as Record<string, unknown>, options);
  if (!options.isCurrent() || !validateConnectedMessageRead(raw, request, session.sid, options.identity))
    throw new Error("connection_changed");
  return raw;
}

/** Display conversion never replaces or round-trips the provider's exact ID. */
export function connectedMessageCreatedTime(
  provider: ConnectedMessageReadRequest["provider"], timestamp: string, lang: Lang,
): { dateTime: string; label: string } | null {
  let date: Date;
  if (provider === "slack") {
    if (!/^\d{1,20}\.\d{1,20}$/.test(timestamp)) return null;
    const [seconds, fraction] = timestamp.split(".");
    const millis = BigInt(seconds) * 1000n + BigInt(fraction.slice(0, 3).padEnd(3, "0"));
    if (millis > 8_640_000_000_000_000n) return null;
    // Milliseconds in Date's finite range are safe integers, unlike Slack IDs.
    date = new Date(Number(millis));
  } else date = new Date(timestamp);
  if (!Number.isFinite(date.getTime())) return null;
  return { dateTime: date.toISOString(), label: new Intl.DateTimeFormat(localeTagFor(lang), {
    dateStyle: "medium", timeStyle: "short",
  }).format(date) };
}
